export const buildSetUserPropertiesEvent = (contactId, siteVersion) => ({
  event: 'set_user_properties',
  contactId,
  siteVersion
});

export const buildSetUserPolicyNumbersEvent = (policyNumbers) => ({
  event: 'set_user_policy_numbers',
  policyNumbers: policyNumbers.join(',')
});

export const buildDocumentDownloadEvent = (documentType, policyNumber) => ({
  event: 'document_downloaded',
  documentType,
  policyNumber
});

export const buildSelectPromotionEvent = (promotion) => ({
  event: 'promotion_selected',
  promotion
});

export const buildDocumentUploadEvent = (documentType, policyNumber) => ({
  event: 'document_uploaded',
  documentType,
  policyNumber
});

export const buildWhatsCoveredEvent = (policyNumber) => ({
  event: 'whats_covered_clicked',
  policyNumber
});

export const buildInteractionEvent = (eventType, payload = {}) => ({
  ...payload,
  event: 'element_interaction',
  eventType
});

// Answer bodies are CMS rich text, so their links can't carry React handlers. A dedicated
// event means GTM doesn't need a broad anchor-text trigger that also catches links outside
// the accordions. Its keys stay snake_case because the event is already live; faq_interaction
// pushes faqQuestion, and GTM can send both as the same faq_question GA4 parameter.
export const buildFaqAnswerLinkClickEvent = ({ faqQuestion, linkText, linkUrl }) => ({
  event: 'faq_answer_link_click',
  faq_question: faqQuestion,
  link_text: linkText,
  link_url: linkUrl
});

export const buildTourStartedEvent = () => ({
  event: 'tour_started'
});

export const buildTourStepEvent = (stepIndex, stepTarget) => ({
  event: 'tour_step',
  tourStep: stepIndex + 1,
  // Targets are CSS selectors like '.petContainer'; strip the leading dot for readability.
  // Joyride also accepts element refs, hence the string guard.
  tourStepName: typeof stepTarget === 'string' ? stepTarget.replace(/^\./, '') : undefined
});

// Named for what it measures: a valid form submission, not a consultation that began.
// It fires before generateConsultationUrl, which can fail — so "started" would overcount.
export const buildVetConsultSubmittedEvent = (consultType) => ({
  event: 'vet_consult_submitted',
  consultType
});

// GA4 cannot express "explicitly unset", and sanitize() rebuilds objects via Object.entries, so an
// undefined value would otherwise reach the data layer as a present key. Keeps false and 0.
const withoutAbsentValues = (event) =>
  Object.fromEntries(Object.entries(event).filter(([, value]) => value !== undefined));

export const buildRenewalBannerImpressionEvent = ({
  policyNumber,
  bannerLocation,
  bannerVariant,
  bannerCtaLabel,
  canRenew,
  daysToExpiry
}) =>
  // bannerCtaLabel is absent on My Pets banners for a policy that cannot be renewed yet:
  // banners.js renders no button there.
  withoutAbsentValues({
    event: 'renewal_banner_impression',
    policyNumber,
    bannerLocation,
    bannerVariant,
    bannerCtaLabel,
    canRenew,
    daysToExpiry
  });

export const buildRenewalBannerClickEvent = ({
  policyNumber,
  bannerLocation,
  bannerVariant,
  bannerCtaLabel,
  destinationPage
}) => ({
  event: 'renewal_banner_click',
  policyNumber,
  bannerLocation,
  bannerVariant,
  bannerCtaLabel,
  destinationPage
});

export const buildRenewalHubEntryEvent = ({ policyNumber, entrySource, entrySourcePage }) => ({
  event: 'renewal_hub_entry',
  policyNumber,
  entrySource,
  entrySourcePage
});

export const buildAdjustCoverUnavailableEvent = ({ policyNumber, reason }) => ({
  event: 'adjust_cover_unavailable',
  policyNumber,
  reason
});

// elementType carries the help mechanism, not selectedValue, so selectedValue holds only the
// values a cover control resolved to. Help events pass neither value.
export const buildAdjustCoverInteractionEvent = ({
  policyNumber,
  elementType,
  fieldName,
  selectedValue,
  previousValue
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_interaction',
    policyNumber,
    elementType,
    fieldName,
    selectedValue,
    previousValue
  });

export const buildAdjustCoverSubmitEvent = ({
  policyNumber,
  excess,
  benefitPercentage,
  routineCare,
  boosterCare,
  annualBenefitLimit,
  newPremium,
  newPremiumAnnual,
  premiumDirection,
  claimExampleBill,
  claimExampleWePay,
  isCoverAdjusted
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_submit',
    policyNumber,
    excess,
    benefitPercentage,
    routineCare,
    boosterCare,
    annualBenefitLimit,
    newPremium,
    newPremiumAnnual,
    premiumDirection,
    claimExampleBill,
    claimExampleWePay,
    isCoverAdjusted
  });

export const buildAdjustCoverSubmitResultEvent = ({ policyNumber, result }) => ({
  event: 'adjust_cover_submit_result',
  policyNumber,
  result
});

// newPremium is per payment period, so it needs the frequency and the annualised figure to be
// comparable across customers.
export const buildAdjustCoverConfirmationViewEvent = ({
  policyNumber,
  excess,
  benefitPercentage,
  routineCare,
  boosterCare,
  annualBenefitLimit,
  newPremium,
  newPremiumAnnual,
  paymentFrequency
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_confirmation_view',
    policyNumber,
    excess,
    benefitPercentage,
    routineCare,
    boosterCare,
    annualBenefitLimit,
    newPremium,
    newPremiumAnnual,
    paymentFrequency
  });

export const buildFaqInteractionEvent = ({ policyNumber, faqQuestion, interactionType, pageName }) => ({
  event: 'faq_interaction',
  policyNumber,
  faqQuestion,
  interactionType,
  pageName
});
